import { unsafeCSS, html, nothing } from "lit";
import { query, property } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { e } from "../../scoped-registry-mixin-2fqf-E5n.js";
import { waitForEvent } from "../../utilities/events.js";
import { I18nController } from "../../utilities/i18n.js";
import { SlotController } from "../../utilities/slot.js";
import { watch } from "../../utilities/watch.js";
import { LeafIcon } from "../icon/icon.component.js";
import { LeafButton } from "../button/button.component.js";
import { LeafElement } from "../leaf-element.js";
const styles = '@charset "UTF-8";\n/* ------------------------------------*\\\n    #TYPOGRAPHY PLACEHOLDERS\n\\*------------------------------------ */\n.leaf-c-alert--banner {\n  left: 50%;\n  margin-left: -50dvw;\n  margin-right: -50dvw;\n  position: relative;\n  right: 50%;\n  width: 100dvw;\n}\n\n.leaf-c-alert--banner .leaf-c-alert__container {\n  box-sizing: border-box;\n  margin-left: auto;\n  margin-right: auto;\n  padding-left: 1rem;\n  padding-right: 1rem;\n  width: 100%;\n}\n@media (min-width: 35rem) {\n  .leaf-c-alert--banner .leaf-c-alert__container {\n    max-width: 540px;\n  }\n}\n@media (min-width: 48rem) {\n  .leaf-c-alert--banner .leaf-c-alert__container {\n    max-width: 720px;\n  }\n}\n@media (min-width: 60rem) {\n  .leaf-c-alert--banner .leaf-c-alert__container {\n    max-width: 960px;\n  }\n}\n@media (min-width: 75rem) {\n  .leaf-c-alert--banner .leaf-c-alert__container {\n    max-width: 1140px;\n  }\n}\n@media (min-width: 87.5rem) {\n  .leaf-c-alert--banner .leaf-c-alert__container {\n    max-width: 1320px;\n  }\n}\n\n/*\n  Hiding class, making content visible only to screen readers but not visually ("sr" meaning "screen-reader")\n  Is the hidden content read by a screen reader?	- Yes\n  Will the hidden element affect the document layout? - No\n  Will the hidden element’s box model be rendered? - No\n  Does the element detect clicks or focus? - Yes\n  https://css-tricks.com/comparing-various-ways-to-hide-things-in-css/#method-5-the-visually-hidden-class\n*/\n:host {\n  --alert-color-close-button-bg: var(--leaf-base-color-opacity-transparent);\n  --alert-color-close-button-border: var(--leaf-base-color-neutral-100);\n}\n\n.leaf-c-alert {\n  align-self: stretch;\n  color: var(--leaf-color-content-knockout);\n  height: auto;\n}\n\n.leaf-c-alert--subtle {\n  color: var(--leaf-color-content-default);\n}\n\n.leaf-c-alert--standalone,\n.leaf-c-alert--banner {\n  border: var(--leaf-border-width-none) solid var(--leaf-color-border-transparent);\n}\n.leaf-c-alert--standalone .leaf-c-alert__container,\n.leaf-c-alert--banner .leaf-c-alert__container {\n  display: flex;\n  gap: var(--leaf-spacing-8);\n  position: relative;\n}\n\n.leaf-c-alert--standalone {\n  border-radius: var(--leaf-border-radius-default);\n  padding: var(--leaf-spacing-8);\n}\n\n.leaf-c-alert--banner {\n  border-radius: 0;\n  padding: var(--leaf-spacing-8) 0;\n}\n.leaf-c-alert--banner .leaf-c-alert__container {\n  grid-gap: 8px !important;\n}\n@media (min-width: 35rem) {\n  .leaf-c-alert--banner .leaf-c-alert__container {\n    grid-gap: var(--leaf-spacing-24);\n    grid-template-columns: minmax(0, calc(8.3333333333% * 12));\n  }\n}\n\n.leaf-c-alert__message-container {\n  display: flex;\n  flex-direction: column;\n  width: 100%;\n}\n.leaf-c-alert__heading-container {\n  position: relative;\n}\n.leaf-c-alert__heading-container leaf-button {\n  position: absolute;\n  top: -6px;\n  right: 0;\n}\n.leaf-c-alert__message-description {\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n  width: inherit;\n  font-family: var(--leaf-typography-body-default-font-family) !important;\n  font-size: var(--leaf-typography-body-default-font-size) !important;\n  font-weight: var(--leaf-typography-body-default-font-weight) !important;\n  line-height: var(--leaf-typography-body-default-line-height) !important;\n  -webkit-text-decoration: var(--leaf-typography-body-default-text-decoration) !important;\n          text-decoration: var(--leaf-typography-body-default-text-decoration) !important;\n  text-transform: var(--leaf-typography-body-default-text-transform) !important;\n}\n.leaf-c-alert.leaf-c-alert--subtle leaf-button.close-button::part(base), .leaf-c-alert.leaf-c-alert--warning leaf-button.close-button::part(base) {\n  background-color: var(--alert-color-close-button-bg);\n  color: var(--leaf-color-content-default);\n}\n.leaf-c-alert.leaf-c-alert--subtle leaf-button.close-button::part(base):hover, .leaf-c-alert.leaf-c-alert--subtle leaf-button.close-button::part(base):focus, .leaf-c-alert.leaf-c-alert--warning leaf-button.close-button::part(base):hover, .leaf-c-alert.leaf-c-alert--warning leaf-button.close-button::part(base):focus {\n  background-color: var(--alert-color-close-button-bg);\n  border-color: var(--alert-color-close-button-border);\n  color: var(--leaf-color-content-default);\n}\n.leaf-c-alert.leaf-c-alert--subtle leaf-button.close-button::part(base):active, .leaf-c-alert.leaf-c-alert--warning leaf-button.close-button::part(base):active {\n  background-color: var(--alert-color-close-button-bg);\n  border-color: var(--alert-color-close-button-border);\n  color: var(--leaf-color-content-default);\n}\n\n.leaf-c-alert--dismissable .leaf-c-alert__message-container {\n  position: relative;\n  width: calc(100% - 3rem);\n}\n\n.leaf-c-alert--error {\n  background: var(--leaf-color-bg-error-strong);\n}\n.leaf-c-alert--error.leaf-c-alert--subtle {\n  background: var(--leaf-color-bg-error);\n}\n\n.leaf-c-alert--success {\n  background: var(--leaf-color-bg-success-strong);\n}\n.leaf-c-alert--success.leaf-c-alert--subtle {\n  background: var(--leaf-color-bg-success);\n}\n\n.leaf-c-alert--warning {\n  background: var(--leaf-color-bg-warning-strong);\n  color: var(--leaf-color-content-default);\n}\n.leaf-c-alert--warning.leaf-c-alert--subtle {\n  background: var(--leaf-color-bg-warning);\n}\n\n.leaf-c-alert--info {\n  background: var(--leaf-color-bg-info-strong);\n}\n.leaf-c-alert--info.leaf-c-alert--subtle {\n  background: var(--leaf-color-bg-info);\n}\n\n.leaf-c-alert--neutral {\n  background: var(--leaf-color-bg-strong);\n}\n.leaf-c-alert--neutral.leaf-c-alert--subtle {\n  background: var(--leaf-color-bg-default);\n}\n\n.leaf-c-alert--inline {\n  background-color: var(--leaf-color-bg-opacity-transparent) !important;\n  display: flex;\n  gap: var(--leaf-spacing-8);\n}\n.leaf-c-alert--inline.leaf-c-alert--error {\n  color: var(--leaf-color-content-error);\n}\n.leaf-c-alert--inline.leaf-c-alert--error leaf-icon {\n  color: var(--leaf-color-content-error-icon);\n}\n.leaf-c-alert--inline.leaf-c-alert--success {\n  color: var(--leaf-color-content-success);\n}\n.leaf-c-alert--inline.leaf-c-alert--success leaf-icon {\n  color: var(--leaf-color-content-success-icon);\n}\n.leaf-c-alert--inline.leaf-c-alert--warning {\n  color: var(--leaf-color-content-default);\n}\n.leaf-c-alert--inline.leaf-c-alert--warning leaf-icon {\n  color: var(--leaf-color-content-warning-icon);\n}\n.leaf-c-alert--inline.leaf-c-alert--info {\n  color: var(--leaf-color-content-default);\n}\n.leaf-c-alert--inline.leaf-c-alert--info leaf-icon {\n  color: var(--leaf-color-content-info-icon);\n}\n.leaf-c-alert--inline.leaf-c-alert--neutral {\n  color: var(--leaf-color-content-default);\n}\n.leaf-c-alert--inline.leaf-c-alert--neutral leaf-icon {\n  color: var(--leaf-color-content-default);\n}\n\n::slotted([slot=heading]) {\n  margin: var(--leaf-spacing-0) !important;\n  font-family: var(--leaf-typography-label-default-font-family) !important;\n  font-size: var(--leaf-typography-label-default-font-size) !important;\n  font-weight: var(--leaf-typography-label-default-font-weight) !important;\n  line-height: var(--leaf-typography-label-default-line-height) !important;\n  -webkit-text-decoration: var(--leaf-typography-label-default-text-decoration) !important;\n          text-decoration: var(--leaf-typography-label-default-text-decoration) !important;\n  text-transform: var(--leaf-typography-label-default-text-transform) !important;\n}\n\n::slotted([slot=cta]) {\n  display: block;\n  padding: var(--leaf-spacing-8) var(--leaf-spacing-0);\n}\n\nleaf-icon {\n  height: var(--leaf-spacing-24);\n  min-height: var(--leaf-spacing-24);\n  min-width: var(--leaf-spacing-24);\n  width: var(--leaf-spacing-24);\n}';
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};
const _LeafAlert = class _LeafAlert extends e(LeafElement) {
  constructor() {
    super(...arguments);
    this.i18nController = new I18nController(this);
    this.slotController = new SlotController(this);
    this.variant = "standalone";
    this.type = "error";
    this.subtle = false;
    this.open = false;
    this.dismissable = false;
  }
  get icon() {
    const icons = {
      info: {
        name: "lightbulb",
        label: this.i18nController.term("alert_info")
      },
      neutral: {
        name: "info",
        label: this.i18nController.term("alert_neutral")
      },
      warning: {
        name: "error",
        label: this.i18nController.term("alert_warning")
      },
      success: {
        name: "check-circle",
        label: this.i18nController.term("alert_success")
      },
      error: {
        name: "warning",
        label: this.i18nController.term("alert_error")
      }
    };
    return icons[this.type];
  }
  firstUpdated() {
    this.alert.hidden = !this.open;
  }
  async handleOpenChange() {
    if (this.open) {
      this.emit("leaf:show");
      this.alert.hidden = false;
      this.emit("leaf:after-show");
    } else {
      this.emit("leaf:hide");
      this.alert.hidden = true;
      this.emit("leaf:after-hide");
    }
  }
  /** handles close click on close button */
  handleClose() {
    this.hide();
  }
  async show() {
    if (!this.open) {
      this.open = true;
      return waitForEvent(this, "leaf:after-show");
    }
  }
  async hide() {
    if (this.open) {
      this.open = false;
      return waitForEvent(this, "leaf:after-hide");
    }
  }
  render() {
    const btnSubtle = this.type === "warning" ? true : this.subtle;
    const componentClassName = {
      "leaf-c-alert": true,
      "leaf-c-alert--error": this.type === "error",
      "leaf-c-alert--success": this.type === "success",
      "leaf-c-alert--warning": this.type === "warning",
      "leaf-c-alert--neutral": this.type === "neutral",
      "leaf-c-alert--info": this.type === "info",
      "leaf-c-alert--standalone": this.variant === "standalone",
      "leaf-c-alert--banner": this.variant === "banner",
      "leaf-c-alert--inline": this.variant === "inline",
      "leaf-c-alert--subtle": this.subtle && this.variant !== "inline",
      "leaf-c-alert--dismissable": this.dismissable,
      "leaf-c-alert--open": this.open
    };
    const closeButton = this.dismissable ? html`<leaf-button
          part="close-button"
          class="close-button"
          ?inverted=${!btnSubtle}
          size="lg"
          variant="tertiary"
          @click=${() => this.handleClose()}
        >
          <leaf-icon slot="icon-before" label="${this.i18nController.term("close")}" name="close"></leaf-icon>
        </leaf-button>` : nothing;
    if (this.variant == "inline") {
      return html` <div part="base" class=${classMap(componentClassName)}>
        <leaf-icon name=${this.icon.name} label="${this.icon.label}"></leaf-icon>
        <slot></slot>
      </div>`;
    } else {
      return html`
        <div part="base" class=${classMap(componentClassName)}>
          <div class="leaf-c-alert__container">
            <leaf-icon name=${this.icon.name} label="${this.icon.label}"></leaf-icon>
            <div class="leaf-c-alert__message-container">
              <div class="leaf-c-alert__heading-container"><slot name="heading"></slot>${closeButton}</div>
              <div class="leaf-c-alert__message-description">
                <slot></slot>
              </div>
              ${this.slotController.hasNamedSlot("cta") ? html`<div class="leaf-c-alert__button-container"><slot name="cta"></slot></div>` : nothing}
            </div>
          </div>
        </div>
      `;
    }
  }
};
_LeafAlert.styles = unsafeCSS(styles);
_LeafAlert.elementDefinitions = {
  "leaf-button": LeafButton,
  "leaf-icon": LeafIcon
};
let LeafAlert = _LeafAlert;
__decorateClass([
  query(".leaf-c-alert")
], LeafAlert.prototype, "alert", 2);
__decorateClass([
  property({ reflect: true })
], LeafAlert.prototype, "variant", 2);
__decorateClass([
  property({ reflect: true })
], LeafAlert.prototype, "type", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], LeafAlert.prototype, "subtle", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], LeafAlert.prototype, "open", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], LeafAlert.prototype, "dismissable", 2);
__decorateClass([
  watch("open", { waitUntilFirstUpdate: true })
], LeafAlert.prototype, "handleOpenChange", 1);
export {
  LeafAlert
};
//# sourceMappingURL=alert.component.js.map
